'use strict'

import { Checks } from './Checks'
import { LanguageEnum } from 'utils/LanguageEnum'
import { LOCAL_KEY_CURRENT_LANGUAGE } from '../constants/StorageKeys'
import pinyin from 'js-pinyin'
const OSS = require('ali-oss')

// 函数防抖
export const throttle = function (fn, gapTimeInMs) {
  if (!gapTimeInMs) {
    gapTimeInMs = 1000
  }

  let lastTime
  return function () {
    let nowTime = new Date()
    if (nowTime - lastTime > gapTimeInMs || !lastTime) {
      lastTime = nowTime
      fn.apply(this, arguments) // 将this和参数传给原函数
    }
  }
}

// 将EPOC以来的秒数格式化为指定样式，如'2018-08-21 12:12:12'
export function formatTimeInSec (timeSeconds, pattern) {
  const SIGN_REGEXP = /([yMdhsm])(\1*)/g
  let date = new Date(timeSeconds * 1000)
  pattern = pattern || 'yyyy-MM-dd hh:mm:ss'
  function padding (s, len) {
    len = len - (s + '').length
    for (var i = 0; i < len; i++) { s = '0' + s }
    return s
  }

  let s = pattern.replace(SIGN_REGEXP, function ($0) {
    switch ($0.charAt(0)) {
      case 'y': return padding(date.getFullYear(), $0.length)
      case 'M': return padding(date.getMonth() + 1, $0.length)
      case 'd': return padding(date.getDate(), $0.length)
      case 'w': return date.getDay() + 1
      case 'h': return padding(date.getHours(), $0.length)
      case 'm': return padding(date.getMinutes(), $0.length)
      case 's': return padding(date.getSeconds(), $0.length)
    }
  })
  return s
}

// 将给定毫秒时间戳格式化为 '8月15日' 或 '11-Jul' 的形式，根据当前给定的语言
export function formatDayMonth (timestamp, lang) {
  if (lang === LanguageEnum.CHINESE) {
    return formatTimeInSec(timestamp / 1000, 'M月d日')
  } else {
    const MONTH_ACRONOYM_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    let date = new Date()
    date.setTime(timestamp)
    return date.getDate() + '-' + MONTH_ACRONOYM_EN[date.getMonth()]
  }
}

/**
 * 输入对象形如 { "factor": 6, "unit": 41, "value": 23276114 }
 * 输出浮点数 23.276114
 */
export function formatObjData (objData) {
  if (!objData) return ''
  if (!objData.factor) return objData.value
  let objValue = objData.value / Math.pow(10, objData.factor)

  return String(objValue).includes('.') ? objValue.toFixed(2) : objValue
}

/**
 * 使用dto与vo的对应关系表来将dto转化为vo
 * @param {Object} mapping e.g. /src/views/planting-report-export/chartsMapping.js
 * @param {*} dto 接口返回的res
 * @returns {*} vo
 */
export function convertDto2VoByMapping (mapping, dto) {
  if (!Checks.isPlainObject(mapping) || !Checks.isArrayOrPlainObject(dto)) {
    return dto
  }

  const FIELD_NAME_SEPARATOR = '>'
  const getSubMapping = (source, target) => mapping[source + FIELD_NAME_SEPARATOR + target] || mapping[source]
  const fieldNameMappingMap =
        Object.keys(mapping)
          .reduce((map, key) => {
            if (Checks.isNotString(key)) {
              console.warn('invalid mapping key type! key: ', key)
              return map
            }

            const [source, target = source] = key.split(FIELD_NAME_SEPARATOR)
            map[source] = target

            return map
          }, {})

  if (Checks.isArray(dto)) {
    return dto.map(element => convertDto2VoByMapping(mapping, element))
  } else if (Checks.isPlainObject(dto)) {
    return Object.keys(dto)
      .reduce((vo, key) => {
        const sourceName = key
        const targetName = fieldNameMappingMap[sourceName] || sourceName

        vo[targetName] = convertDto2VoByMapping(getSubMapping(sourceName, targetName), dto[sourceName])
        return vo
      }, {})
  } else {
    return dto
  }
}

export function getErrorMessage (err) {
  let message = ''

  if (!err) {
    return message
  }

  if (typeof err === 'string') {
    message = err
  } else if (err instanceof Error) {
    message = err.message
  } else {
    message = err.message || (err.data && err.data.message)
  }

  return message || ''
}

// 由于目前的依赖版本不支持Promise.allSettled方法，因此这里用polyfill替代
export const JYPromiseAllSettledPolyfill = (promises) => Promise.all(
  promises.map(p => p
    .then(value => ({
      status: 'fulfilled',
      value
    }))
    .catch(reason => ({
      status: 'rejected',
      reason
    }))
  )
)

export function uploadToOSS (file) {
  let client = OSS({
    region: process.env.OSS_REGION,
    accessKeyId: process.env.OSS_ACCESS_KEY_ID,
    accessKeySecret: process.env.OSS_ACCESS_KEY_SECRET,
    bucket: process.env.OSS_IMAGE_BUCKET
  })

  if (file.imagePath) {
    return Promise.resolve(file)
  }

  return client.put(file.name, file.raw)
    .then(res => Promise.resolve(Object.assign(file, {imagePath: res.url})))
    .catch(err => Promise.reject(err))
}

// 构造地块全名，形如 地主姓名_基地名称_地块名称_品种名称。其中只有品种名称可以为空。
export function constructPlotFullName (farmerName, farmName, plotName, varietyName) {
  if (!farmerName) {
    farmerName = '未知薯农'
  }
  if (!farmName) {
    farmName = '未知基地'
  }
  if (!plotName) {
    plotName = '未知地块'
  }

  // 过滤掉基地名称中的薯农姓名
  farmName = farmName.replace(farmerName, '')

  // 过滤掉地块名称中的薯农姓名，基地名称以及尾部的品种名称
  const farmNameMain = farmName.lastIndexOf('-') === -1 ? farmName : farmName.substring(0, farmName.lastIndexOf('-')) // 基地主体名称，去除了'-2022'之类的后缀
  plotName = plotName.replace(farmerName + '-', '').replace(farmerName + '_', '').replace(farmerName, '').replace(farmNameMain, '')
  if (varietyName && plotName.endsWith('_' + varietyName)) {
    plotName = plotName.substring(0, plotName.lastIndexOf('_' + varietyName))
  }
  if (varietyName && plotName.endsWith(varietyName)) {
    plotName = plotName.substring(0, plotName.lastIndexOf(varietyName) - 1)
  }
  if (plotName.endsWith('_')) {
    plotName = plotName.substring(0, plotName.length - 1)
  }
  if (plotName.endsWith('-')) {
    plotName = plotName.substring(0, plotName.length - 1)
  }

  return farmerName + '_' + farmName + '_' + plotName + (!varietyName ? '' : '_' + varietyName)
}

/*
 * 用指定的文本创建水印Canvas，如不指定文本则默认为'保卫土豆'。
 * 注意，必须在页面 mounted 之后调用
 */
export function createWaterMarkCanvas (text) {
  let date = new Date()
  let waterMarkCanvas = document.createElement('canvas')
  waterMarkCanvas.width = 300
  waterMarkCanvas.height = 140
  let ctx = waterMarkCanvas.getContext('2d')
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.globalAlpha = 0.06
  ctx.font = '16px Microsoft Yahei'
  ctx.translate(100, 80)
  ctx.rotate(-Math.PI / 6)
  ctx.fillText(text || 'Copyright©' + date.getFullYear() + ' 保卫土豆', 0, 0)
  return waterMarkCanvas
}

/* 按Echarts图标区域大小计算指定字号缩放后的值， */
export function scaleEchartFontSize (chartDom, fontSize) {
  if (!chartDom || !fontSize || isNaN(fontSize)) {
    return null
  }

  let myChartHeight = (window.getComputedStyle(chartDom).height || '').replace('px', '')
  let myChartWidth = (window.getComputedStyle(chartDom).width || '').replace('px', '')

  if (isNaN(myChartHeight) && isNaN(myChartWidth)) {
    return null
  }

  if (isNaN(myChartHeight)) {
    myChartHeight = 480
  }

  if (isNaN(myChartWidth)) {
    myChartWidth = 1280
  }

  const fontSizeByHeight = (myChartHeight / 30) * fontSize / 16
  const fontSizeByWidth = (myChartWidth / 80) * fontSize / 16
  const properFontSize = Math.max(8, // 最小不小于8px
    Math.ceil(fontSizeByHeight > fontSizeByWidth ? fontSizeByHeight : fontSizeByWidth))

  // console.log('scaleEchartFontSize() scaled ' + fontSize + ' to ' + properFontSize)
  return properFontSize
}

/* 启用Echarts的下载工具，可通过该工具将Echarts下载为png图片 */
export function enableEchartSavingImageTool (page, chart, chartOption) {
  if (!chartOption.toolbox) {
    chartOption.toolbox = {}
    chartOption.toolbox.show = true
  }
  if (!chartOption.toolbox.feature) {
    chartOption.toolbox.feature = {}
  }

  const chartTitle = chartOption.title ? chartOption.title.text : '无标题图'
  chartOption.toolbox.feature.mySaveImg = {
    show: true,
    title: window[LOCAL_KEY_CURRENT_LANGUAGE] === LanguageEnum.CHINESE ? '保存为图片' : 'Save As Image',
    icon: 'path://M 384 448 V 110 h 230 v 310 h 180 l -300 324 l -324 -324 h 180 Z m -170 450 h 600 v 60 H 140 v -60 Z',
    onclick: function () {
      let picInfo = chart.getDataURL({
        type: 'png',
        pixelRatio: 4 // 放大4倍下载
      }) // 获取到的是一串base64信息
      const link = document.createElement('a')
      link.download = chartTitle
      link.style.display = 'none'
      link.href = picInfo
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(link.href) // 释放URL 对象
      document.body.removeChild(link)
    }
  }

  return chartOption
}

// 获取给定时间戳代表的那一天的初始时刻的时间戳
export function getStartTimestampOfDay (timestamp) {
  let date = new Date()
  date.setTime(timestamp)
  date.setMilliseconds(0)
  date.setSeconds(0)
  date.setMinutes(0)
  date.setHours(0)
  return date.getTime()
}

// 判定给定的年份(四位数字)是否为闰年
export function isLeapYear (year) {
  return !isNaN(year) && ((year % 100 === 0 && year % 400 === 0) || (year % 100 !== 0 && year % 4 === 0))
}

// 面积单位转换。area是面积，reverse为true时从亩转为英亩，否则从英亩转为亩
export function translateMu2Acre (area, reverse) {
  if (!reverse) {
    return (area / 6.0702846)
  }
  return (area * 6.0702846)
}

// 产量单位转换。production是产量，reverse为true时从吨/亩转为英担/英亩，否则从英担/英亩转为吨/亩
export function translateTonPerMu2CWTPerAcre (production, digitalNum, reverse) {
  if (!reverse) {
    return (1000 * production / 8.369022)
  }
  return (production * 8.369022 / 1000)
}

// 将英文字符串转为首字母大写，对其他语言不做处理
export function capitalizeString (str) {
  return str && str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

/** 判断给定的数是不是整数 */
export function isInteger (num) {
  return typeof num === 'number' && (num % 1 === 0)
}

/** 求两个正整数的最大公约数 */
export function calcGreatestCommonDivisor (a, b) {
  if (!isInteger(a) || !isInteger(b) || a < 0 || b < 0) {
    console.error('calcGreatestCommonDivisor() invalid parameters', a, b)
    return null
  }
  if (b === 0) {
    return a
  }
  let r = a % b
  return calcGreatestCommonDivisor(b, r)
}

/** 求两个正整数的最小公倍数 */
export function calcLeastCommonMultiple (a, b) {
  if (!isInteger(a) || !isInteger(b) || a <= 0 || b <= 0) {
    console.error('calcLeastCommonMultiple() invalid parameters', a, b)
    return null
  }
  return (a * b) / calcGreatestCommonDivisor(a, b)
}

/** 根据指标值评价的中文描述获取符号，例如 '偏高' 对应符号 ↑，'偏高' 对应符号 ↓ */
export function getEvaluationSignBySummaryCn (summaryCn) {
  if (!summaryCn) {
    return ''
  }
  if (summaryCn.indexOf('高') >= 0 || summaryCn.indexOf('多') >= 0 ||
    summaryCn.indexOf('快') >= 0 || summaryCn.indexOf('碱') >= 0 ||
    summaryCn.indexOf('强') >= 0 || summaryCn.indexOf('重') >= 0 ||
    summaryCn === '盐土') {
    return '↑'
  } else if (summaryCn.indexOf('低') >= 0 || summaryCn.indexOf('少') >= 0 ||
    summaryCn.indexOf('慢') >= 0 || summaryCn.indexOf('酸') >= 0 ||
    summaryCn.indexOf('弱') >= 0 || summaryCn.indexOf('轻') >= 0 ||
    summaryCn === '非盐化') {
    return '↓'
  }
  return ''
}

/** 将地块或基地名称翻译成英文 */
export function translateOrgOrPlotName (farmName) {
  if (!farmName) {
    return farmName
  }

  if (farmName.startsWith('百事') || farmName.startsWith('百事中国')) {
    farmName = farmName.replace('百事中国', 'PepsiCo ').replace('百事', 'PepsiCo ')
  }
  farmName = farmName.replace('农业种植专业合作社', ' cooperative')
    .replace('种植专业合作社', ' cooperative')
    .replace('农业专业合作社', ' cooperative')
    .replace('农业合作社', ' cooperative')
    .replace('专业合作社', ' cooperative')
    .replace('农场', ' farm')
    .replace('基地', ' farm')
    .replace('青岛', 'QingDao ')
    .replace('胶州', 'JiaoZhou ')
    .replace('临沂', 'LinYi ')
    .replace('易普润', 'Van Iperen ')
    .replace('雪川', ' Snowvalley')
    .replace('实验', ' demonstration')
    .replace('滴灌 Terra', 'Terra applied')
    .replace('雪育1号', 'snowvalley#1')
    .replace('叶喷 Good', 'Good applied')
    .replace('大西洋', 'Atlantic')
  farmName = farmName.replace('马铃薯', ' potato ').replace('智慧', ' smart ').replace('地块', ' field')
  farmName = pinyin.getFullChars(farmName)
  return farmName
}

/** 计算播种日期到给定日期的播后天数，如果无法计算则返回空字符串''，否则返回一个正整数 */
export function calculateDAP (collectTimestamp, sowTimestamp) {
  if (!sowTimestamp || !collectTimestamp || collectTimestamp < sowTimestamp) {
    return ''
  }
  return 1 * ((getStartTimestampOfDay(collectTimestamp) - getStartTimestampOfDay(sowTimestamp)) / (24 * 3600 * 1000)).toFixed(0)
}

/** 计算出苗日期到给定日期的苗后天数，如果无法计算则返回空字符串''，否则返回一个正整数 */
export function calculateDAE (collectTimestamp, emergenceTimestamp) {
  return calculateDAP(collectTimestamp, emergenceTimestamp)
}

/**
 * 恰当保留小数位数，并且删除结尾多余的零。
 * 例如 (9.27999, 3) 返回 9.28
 *
 * @param figure 待处理的小数
 * @param maxDigitCnt 最多保留的小数位数
 * @return 保留小数位数后的字符串，注意是字符串不是数字
 */
export function roundDigitNumber (figure, maxDigitCnt) {
  let power = Math.pow(10, maxDigitCnt)
  let rounded = Math.round(figure * power) + ''
  let zeroCntFromTail = 0
  for (let i = rounded.length - 1; i >= rounded.length - maxDigitCnt - 1 && i >= 0; i--) {
    if (rounded.charAt(i) !== '0') {
      break
    }
    zeroCntFromTail++
  }
  return (rounded / power).toFixed(maxDigitCnt < zeroCntFromTail ? 0 : maxDigitCnt - zeroCntFromTail)
}

/** 防抖动函数 */
export function debounce (func, timeout = 300) {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => { func.apply(this, args) }, timeout)
  }
}



// WEBPACK FOOTER //
// ./src/utils/utils.js